/* eslint-disable react-native/no-inline-styles */
import * as React from "react";
import { StyleSheet, Image, View } from "react-native";
import { Button, Paragraph, useTheme } from "react-native-paper";
import { Block } from "@block";
import { QueueSpacing, StackSpacing } from "@components/spacing";
import QRCode from "react-native-qrcode-svg";
import { useTranslation } from "react-i18next";
import { spacing } from "@styles/spacing";
import { useDesktopMediaQuery } from "@media-query";
import { RegisterOptions, useForm } from "react-hook-form";
import { InputText } from "../form/input";
import { VERIFICATION, RESPONSEDATA } from "../../services/services-model.type";
import { Countdown } from "../countdown/countdown";
import { ServiceCode } from "app/shared/services/api-path";
import { testProps } from "app/shared/util/test-props";
import images from "app/shared/styles/images";
import { useDispatch } from "react-redux";
import { showModal } from "app/modules/modal-management/reducer/action";

interface ConfirmOTPProps {
  data?: RESPONSEDATA;
  serviceCode?: string;
  onSubmit: Function;
  goBack: Function;
  isReset?: boolean;
  errorEndOTP?: boolean;
  verification?: VERIFICATION;
}

type FormData = {
  rqStsEq: string;
  challengeValue: string;
  otpCode: string;
};

enum FormEnum {
  rqStsEq,
  challengeValue,
  otpCode,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

const ConfirmOTP = (props: ConfirmOTPProps) => {
  const { data, serviceCode = "", onSubmit, goBack, isReset, errorEndOTP = false, verification } = props;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const isDesktop = useDesktopMediaQuery();
  const dispatch = useDispatch();

  const [endTimer, setEndTimer] = React.useState(false);

  const { control, handleSubmit, reset, setFocus, clearErrors, setValue } = useForm<FormData>({
    defaultValues: {
      otpCode: "",
    },
    mode: "onChange",
  });

  // Tạo validate Schema
  const validateSchemeForm: ValidDataSchemeFormData = {
    rqStsEq: {
      required: {
        value: false,
        message: "",
      },
    },
    challengeValue: {
      required: {
        value: false,
        message: "",
      },
    },
    otpCode: {
      required: {
        value: true,
        message: t("COMMON_OTP_CHUA_NHAP"),
      },
      minLength: {
        value: 6,
        message: t("COMMON_OTP_KHONG_HOP_LE"),
      },
    },
  };

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        qrCodeDescription: { fontWeight: "700" },
        fieldFormStyles: { color: colors.text, width: 200, fontWeight: "700", fontSize: 16 },
        buttonGoBackStyles: { alignSelf: "center", backgroundColor: colors.backgroundForm },
        buttonAcceptStyles: { alignSelf: "center" },
        desktopShowStyles: {
          display:
            isDesktop &&
            (data?.CHALLENGE_VALUE?.length ||
              ((serviceCode === ServiceCode.CARD || serviceCode === ServiceCode.AUTO_TOP_UP || serviceCode === ServiceCode.AUTO_BILLING) &&
                verification?.challengeValue?.length))
              ? "flex"
              : "none",
        },
        mobileShowStyles: {
          display:
            !isDesktop &&
            (data?.CHALLENGE_VALUE?.length ||
              ((serviceCode === ServiceCode.CARD || serviceCode === ServiceCode.AUTO_TOP_UP || serviceCode === ServiceCode.AUTO_BILLING) &&
                verification?.challengeValue?.length))
              ? "flex"
              : "none",
        },
        challengeDisplay: {
          display:
            data?.CHALLENGE_VALUE?.length ||
            ((serviceCode === ServiceCode.CARD || serviceCode === ServiceCode.AUTO_TOP_UP || serviceCode === ServiceCode.AUTO_BILLING) &&
              verification?.challengeValue?.length)
              ? "flex"
              : "none",
        },
        otpDisplay: { display: !endTimer ? "flex" : "none" },
      }),
    [colors.text, colors.backgroundForm, isDesktop, data?.CHALLENGE_VALUE, serviceCode, verification?.challengeValue, endTimer],
  );

  const textInputOTP: object = {
    maxLength: 6,
    disabled: endTimer,
    autoFocus: true,
  };

  const getRqStsEq = React.useCallback(() => {
    switch (serviceCode) {
      case ServiceCode.PAYMENT_BILLING:
        return data?.EBINQUIRY?.RQSTSEQ || "";
      case ServiceCode.TERM_DEPOSIT_OPEN:
      case ServiceCode.TERM_DEPOSIT_TOPUP:
      case ServiceCode.TERM_DEPOSIT_STATEMENT:
        return data?.EBACCOUNT_DEPOSITS?.RQSTSEQ || "";
      case ServiceCode.CARD:
        return verification?.requestId || "";
      case ServiceCode.AUTO_TOP_UP:
      case ServiceCode.AUTO_BILLING:
        return verification?.transId || "";
      default:
        return data?.TRANSFER?.RQSTSEQ || "";
    }
  }, [data, serviceCode, verification]);

  const handlerCancel = () => {
    goBack();
  };

  const onPressAccept = (values: FormData): void => {
    onSubmit(values);
  };

  const onPressCancel = () => {
    if (endTimer) handlerCancel();
    else
      dispatch(
        showModal({
          modalType: "multiAction",
          modalProps: {
            open: true,
            message: t("COMMON_QUY_KHACH_MUON_HUY_GIAO_DICH"),
            acceptModal: () => handlerCancel(),
          },
        }),
      );
  };

  const onCompleteCountDown = () => {
    setEndTimer(true);
    clearErrors("otpCode");
  };

  const getChallengeValue = () => {
    const value =
      serviceCode === ServiceCode.CARD || serviceCode === ServiceCode.AUTO_TOP_UP || serviceCode === ServiceCode.AUTO_BILLING
        ? verification?.challengeValue
        : data?.CHALLENGE_VALUE;
    console.log("getChallengeValue: ", value);
    return value;
  };

  const FieldForm = ({ fieldTitle = "" }) => (
    <View>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFormStyles}>{fieldTitle}</Paragraph>
      <StackSpacing size="medium" />
    </View>
  );

  const InstructionForm = (
    <View style={[{ flex: 1 }]}>
      <Image
        style={{
          borderRadius: 24,
          height: undefined,
          width: "100%",
          aspectRatio: isDesktop ? 760 / 385 : 800 / 600,
        }}
        source={isDesktop ? images.otp_guild_web : images.otp_guild_mobile}
      />
    </View>
  );

  const Form = (
    <Block p={spacing.huge} br={12} row={isDesktop ? true : false} flex={1} bg={colors.backgroundForm}>
      <Block flex={1}>
        <InputText
          name={"rqStsEq"}
          rules={validateSchemeForm.rqStsEq}
          inputProps={{ editable: false }}
          control={control}
          label={t("COMMON_PHIEN_GIAO_DICH")}
        />
        <InputText
          name={"challengeValue"}
          rules={validateSchemeForm.challengeValue}
          inputProps={{ editable: false }}
          style={styles.challengeDisplay}
          control={control}
          label={t("COMMON_MA_GD")}
        />
        <InputText
          name={"otpCode"}
          rules={validateSchemeForm.otpCode}
          inputProps={textInputOTP}
          control={control}
          style={styles.otpDisplay}
          label={t("COMMON_NHAP_MA_XA_THUC")}
        />
        <Countdown onComplete={onCompleteCountDown} errorEndOTP={errorEndOTP} />
      </Block>
      <QueueSpacing size="large" />
      <View style={styles.desktopShowStyles}>
        <Block center middle flex={1}>
          <StackSpacing size={"small"} />
          <Paragraph style={styles.qrCodeDescription}>{t("COMMON_QUET_LAY_MA_XA_THUC")}</Paragraph>
          <StackSpacing size={"small"} />
          {data?.CHALLENGE_VALUE?.length ||
          ((serviceCode === ServiceCode.CARD || serviceCode === ServiceCode.AUTO_TOP_UP || serviceCode === ServiceCode.AUTO_BILLING) &&
            verification?.challengeValue?.length) ? (
            <View {...testProps("qrCode")}>
              <QRCode value={getChallengeValue()} size={120} color={colors.backdrop} ecl="Q" />
            </View>
          ) : (
            <Block />
          )}
        </Block>
      </View>
    </Block>
  );

  const QRCodeMobile = (
    <View style={styles.mobileShowStyles}>
      <Block center middle flex={1}>
        <StackSpacing size={"small"} />
        <Paragraph style={styles.qrCodeDescription}>{t("COMMON_QUET_LAY_MA_XA_THUC")}</Paragraph>
        <StackSpacing size={"small"} />
        {data?.CHALLENGE_VALUE?.length ||
        ((serviceCode === ServiceCode.CARD || serviceCode === ServiceCode.AUTO_TOP_UP || serviceCode === ServiceCode.AUTO_BILLING) &&
          verification?.challengeValue?.length) ? (
          <View {...testProps("qrCodeMobile")}>
            <QRCode
              value={
                serviceCode === ServiceCode.CARD || serviceCode === ServiceCode.AUTO_TOP_UP || serviceCode === ServiceCode.AUTO_BILLING
                  ? verification?.challengeValue
                  : data?.CHALLENGE_VALUE
              }
              size={120}
              color={colors.backdrop}
            />
          </View>
        ) : (
          <Block />
        )}
      </Block>
    </View>
  );

  const ButtonRow = (
    <Block row middle center>
      <Button mode="contained" style={styles.buttonGoBackStyles} onPress={onPressCancel}>
        {t("COMMON_QUAY_LAI")}
      </Button>
      <QueueSpacing size="large" />
      <Button
        onPress={handleSubmit(onPressAccept)}
        mode="contained"
        style={styles.buttonAcceptStyles}
        disabled={endTimer}
        {...testProps("otpBtn")}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  React.useEffect(() => {
    if (isReset) reset();
  }, [isReset, reset]);

  React.useEffect(() => {
    setValue("rqStsEq", getRqStsEq());
    if (serviceCode === ServiceCode.CARD || serviceCode === ServiceCode.AUTO_TOP_UP || serviceCode === ServiceCode.AUTO_BILLING) {
      setValue("challengeValue", verification?.challengeValue || "");
    } else setValue("challengeValue", data?.CHALLENGE_VALUE || "");
  }, [data, getRqStsEq, serviceCode, setValue, verification]);

  React.useEffect(() => {
    setFocus("otpCode");
  }, [setFocus]);

  return (
    <>
      <Block ph={isDesktop ? "10%" : spacing.large} row={isDesktop ? true : false} pt={spacing.medium}>
        <FieldForm fieldTitle={t("COMMON_XAC_THUC_GD")} />
        {Form}
      </Block>
      {QRCodeMobile}
      <Block ph={isDesktop ? "10%" : spacing.large} row={isDesktop ? true : false} pt={spacing.medium}>
        <FieldForm fieldTitle={t("COMMON_HUONG_DAN")} />
        {InstructionForm}
      </Block>
      <Block ph={isDesktop ? "10%" : spacing.large} row={isDesktop ? true : false} pt={spacing.medium} pb={spacing.medium}>
        <FieldForm fieldTitle={""} />
        <Block flex={1} center middle>
          {ButtonRow}
        </Block>
      </Block>
    </>
  );
};

export default ConfirmOTP;
